import { Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { Chart } from 'chart.js';
import * as moment from 'moment';
import { DateRange } from '../../../../redux/models/statistics/date-range';
import { Statistics } from '../../../../redux/models/statistics/statistics';
import { getLocale } from '../../../../redux/services/utils/data-helpers';

@Component({
  standalone: false,
  selector: 'app-bookings-and-revenues',
  templateUrl: './bookings-and-revenues.component.html',
  styleUrls: ['./bookings-and-revenues.component.scss']
})
export class BookingsAndRevenuesComponent implements OnDestroy {
  @ViewChild('bookingsAndRevenues', { static: true })
  private readonly bookingsAndRevenuesChart: ElementRef<HTMLCanvasElement>;
  private chart: Chart;
  private datesetToTooltipLabels: { [key: number]: string[] };
  private readonly months: { [key: number]: string };

  constructor() {
    moment.locale(getLocale());
    this.months = moment.monthsShort();
  }

  @Input()
  set statistics(statistics: Statistics[]) {
    if (statistics) {
      this.configureBookingsAndRevenuesChart(statistics);
    }
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private configureBookingsAndRevenuesChart(statistics: Statistics[]): void {
    this.datesetToTooltipLabels = {};

    const data = {
      labels: this.getStatisticLabels(statistics[0]),
      datasets: statistics.map((statistic, index) => {
        this.configureTooltips(statistic, index);
        return {
          label: this.getDatasetName(statistic.dateRange),
          //TODO: format values of data points and Y axis labels
          data: statistic.dataPoints.map(point => Number((+point.value).toFixed(2))),
          borderColor: DATASET_COLORS[index],
          backgroundColor: DATASET_COLORS[index],
          fill: false,
          tension: 0
        };
      })
    };

    const config = this.buildChartConfig(data);

    if (!this.chart) {
      this.chart = new Chart(this.bookingsAndRevenuesChart.nativeElement, config);
      return;
    }

    this.chart.config = config;
    this.chart.update();
  }

  private getTooltipLabel(): (tooltipItem) => string {
    return tooltipItem => {
      const tooltipLabels = this.datesetToTooltipLabels[tooltipItem.datasetIndex];
      return `${tooltipLabels[tooltipItem.index]}: ${tooltipItem.value}`;
    };
  }

  private getDatasetName(dateRange: DateRange): string {
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);

    return startDate.getFullYear() === endDate.getFullYear()
      ? `${startDate.getFullYear()}`
      : `${startDate.getFullYear()} - ${endDate.getFullYear()}`;
  }

  private getStatisticLabels(statistic: Statistics): string[] {
    const startDate = new Date(statistic.dateRange.startDate);

    const monthDiff = statistic.dataPoints.length;
    const startMonthIndex = startDate.getMonth();
    const statisticLabels = [];
    let monthLabelPosition = 0;

    for (let monthIndex = startMonthIndex; monthIndex < startMonthIndex + monthDiff; monthIndex++) {
      statisticLabels.push(this.getMonthLabel(monthIndex, monthDiff, monthLabelPosition));
      monthLabelPosition++;
    }
    return statisticLabels;
  }

  private getMonthLabel(monthIndex: number, monthDiff: number, monthLabelPosition: number): string {
    if (this.isOverHalfAYear(monthLabelPosition, monthDiff) || this.isOverAYear(monthLabelPosition, monthDiff)) {
      return '';
    }

    return this.months[monthIndex % 12].toUpperCase();
  }

  private isOverHalfAYear(index: number, numberOfMonths: number): boolean {
    return numberOfMonths > 6 && numberOfMonths < 13 && index % 2 !== 0;
  }

  private isOverAYear(index: number, numberOfMonths: number): boolean {
    return numberOfMonths > 12 && index % 3 !== 0;
  }

  private configureTooltips(statistic: Statistics, datasetIndex: number): void {
    const startDate = new Date(statistic.dateRange.startDate);

    const monthDiff = statistic.dataPoints.length;

    if (this.datesetToTooltipLabels[datasetIndex] == null) {
      this.datesetToTooltipLabels[datasetIndex] = [];
    }

    const startMonthIndex = startDate.getMonth();
    let currentYear = startDate.getFullYear();

    for (let index = startMonthIndex; index <= startMonthIndex + monthDiff; index++) {
      const monthIndex = index % 12;
      this.datesetToTooltipLabels[datasetIndex].push(`${currentYear}-${this.months[monthIndex].toUpperCase()}`);
      if (monthIndex === 11) {
        currentYear++;
      }
    }
  }

  private buildChartConfig(data: any): any {
    return {
      data,
      type: 'line',
      options: {
        legend: {
          align: 'end'
        },
        tooltips: {
          callbacks: {
            label: this.getTooltipLabel(),
            title: () => ''
          }
        }
      }
    };
  }
}

const DATASET_COLORS = {
  0: 'rgb(0, 8, 53)',
  1: 'rgb(58, 139, 255)',
  2: 'rgb(197, 213, 249)',
  3: 'rgb(194, 225, 255)'
};
